import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Bookmark, Category } from '../types/bookmark'
import { User } from '../types/user'
import { createAuthMiddleware } from './middleware/authMiddleware'

export interface AppStore {
  // 主题相关
  theme: 'light' | 'dark'
  setTheme: (theme: 'light' | 'dark') => void
  
  // 书签相关
  bookmarks: Bookmark[]
  categories: Category[]
  currentUser: User | null
  setCurrentUser: (user: User | null) => void
  addBookmark: (bookmark: Omit<Bookmark, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => void
  updateBookmark: (id: string, bookmark: Partial<Bookmark>) => void
  deleteBookmark: (id: string) => void
  addCategory: (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => void
  updateCategory: (id: string, category: Partial<Category>) => void
  deleteCategory: (id: string) => void
  importData: (bookmarks: Bookmark[], categories: Category[]) => void
  clearAll: () => void
  clearOtherUsersData: () => void
  getBookmarks: () => Bookmark[]
  getCategories: () => Category[]
}

export const useStore = create<AppStore>()(
  persist(
    createAuthMiddleware((set, get) => ({
      // 主题相关
      theme: 'light',
      setTheme: (theme) => set({ theme }),
      
      // 书签相关
      bookmarks: [],
      categories: [],
      currentUser: null,
      
      setCurrentUser: (user) => {
        set({ currentUser: user })
        if (user) {
          // 用户登录时清理其他用户数据
          get().clearOtherUsersData()
        } else {
          // 用户登出时清理所有数据
          get().clearAll()
        }
      },
      
      addBookmark: (bookmark) => {
        const { currentUser } = get()
        if (!currentUser) return
        
        const newBookmark: Bookmark = {
          ...bookmark,
          id: crypto.randomUUID(),
          userId: currentUser.id,
          tags: bookmark.tags || [],
          isFavorite: bookmark.isFavorite || false,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }
        set((state) => ({
          bookmarks: [...state.bookmarks, newBookmark],
        }))
      },

      updateBookmark: (id, bookmark) => {
        const { currentUser } = get()
        if (!currentUser) return
        
        set((state) => ({
          bookmarks: state.bookmarks.map((b) =>
            b.id === id && b.userId === currentUser.id
              ? { ...b, ...bookmark, updatedAt: Date.now() }
              : b
          ),
        }))
      },

      deleteBookmark: (id) => {
        const { currentUser } = get()
        if (!currentUser) return
        
        set((state) => ({
          bookmarks: state.bookmarks.filter(
            (b) => !(b.id === id && b.userId === currentUser.id)
          ),
        }))
      },

      addCategory: (category) => {
        const { currentUser } = get()
        if (!currentUser) return
        
        const newCategory: Category = {
          ...category,
          id: crypto.randomUUID(),
          userId: currentUser.id,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }
        set((state) => ({
          categories: [...state.categories, newCategory],
        }))
      },

      updateCategory: (id, category) => {
        const { currentUser } = get()
        if (!currentUser) return
        
        set((state) => ({
          categories: state.categories.map((c) =>
            c.id === id && c.userId === currentUser.id
              ? { ...c, ...category, updatedAt: Date.now() }
              : c
          ),
        }))
      },

      deleteCategory: (id) => {
        const { currentUser } = get()
        if (!currentUser) return
        
        set((state) => ({
          categories: state.categories.filter(
            (c) => !(c.id === id && c.userId === currentUser.id)
          ),
        }))
      },

      importData: (bookmarks, categories) => {
        const { currentUser } = get()
        if (!currentUser) return
        
        // 只导入属于当前用户的数据
        const userBookmarks = bookmarks.filter(b => b.userId === currentUser.id)
        const userCategories = categories.filter(c => c.userId === currentUser.id)
        
        set(() => ({
          bookmarks: userBookmarks,
          categories: userCategories,
        }))
      },

      clearAll: () => {
        const { theme } = get() // 保存当前主题设置
        set(() => ({
          bookmarks: [],
          categories: [],
          currentUser: null,
          theme, // 恢复主题设置
        }))
      },

      // 新增：清理其他用户的数据
      clearOtherUsersData: () => {
        const { currentUser } = get()
        if (!currentUser) return

        set((state) => ({
          bookmarks: state.bookmarks.filter(b => b.userId === currentUser.id),
          categories: state.categories.filter(c => c.userId === currentUser.id),
        }))
      },

      // 新增：获取当前用户的书签
      getBookmarks: () => {
        const { currentUser, bookmarks } = get()
        if (!currentUser) return []
        return bookmarks.filter(b => b.userId === currentUser.id)
      },

      // 新增：获取当前用户的分类
      getCategories: () => {
        const { currentUser, categories } = get()
        if (!currentUser) return []
        return categories.filter(c => c.userId === currentUser.id)
      },
    })),
    {
      name: 'app-storage',
      partialize: (state) => ({
        theme: state.theme,
        currentUser: state.currentUser,
        // 只持久化当前用户的数据
        bookmarks: state.currentUser 
          ? state.bookmarks.filter(b => b.userId === state.currentUser?.id)
          : [],
        categories: state.currentUser
          ? state.categories.filter(c => c.userId === state.currentUser?.id)
          : [],
      }),
    }
  )
) 